import React from "react";
import { BasePopup } from "./BasePopup";
import DetailItem from "components/card/ItemDetail";
import { Asset } from "types";
import AssetOwnerCard from "components/card/AssetOwnerCard";

export default function AssetDetailsPopup({
  showDetail,
  setShowDetail,
  asset,
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  showDetail: boolean;
  setShowDetail: React.Dispatch<React.SetStateAction<boolean>>;
  asset: Asset;
  children?: React.ReactNode;
}) {
  const assetLocation = [
    asset.asset_location_region,
    asset.asset_location_zone,
    asset.asset_location_woreda,
    asset.asset_location_town,
  ]
    .filter(Boolean) // Remove empty/null/undefined values
    .join(", ");

  // Combine crime location fields into one string
  const crimeLocation = [
    asset.crime_location_region,
    asset.crime_location_zone,
    asset.crime_location_woreda,
    asset.crime_location_town,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <BasePopup
      isOpen={showDetail}
      position="center"
      title={title}
      onClose={() => setShowDetail(false)}
      description={description}
      w="700px"
    >
      <div className="space-y-4">
        {asset ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              {/* Basic Asset Information */}
              <DetailItem
                label="Asset Unique ID"
                value={asset.asset_unique_id}
              />
              <DetailItem label="Asset Category" value={asset.asset_category} />
              <DetailItem label="Asset Number" value={asset.asset_number} />
              <DetailItem label="Quantity" value={asset.quantity} />
              <DetailItem
                label="Estimated Value"
                value={asset.estimation_of_money}
              />
              <DetailItem
                label="Asset Condition"
                value={asset.asset_condition}
              />
              <DetailItem label="Owned Date" value={asset.asset_owned_date} />

              <DetailItem
                label="Relationship with Suspect"
                value={asset.owner_relationship_with_suspect}
              />
              <DetailItem label="Spouse" value={asset.spouse} />
              <DetailItem
                label="Is Owner Above 18"
                value={asset.is_owner_above_18 ? "Yes" : "No"}
              />
              <DetailItem
                label="Is Suspect the Owner"
                value={asset.is_the_suspect_the_owner ? "Yes" : "No"}
              />

              {/* Asset Location */}
              <DetailItem label="Asset Location" value={assetLocation} />

              {/* Crime Information */}
              <DetailItem label="Crime Category" value={asset.crime_category} />
              <DetailItem label="Case Condition" value={asset.case_condition} />
              <DetailItem
                label="How Asset Acquired"
                value={asset.how_asset_acquired}
              />

              {/* Crime Location */}
              <DetailItem label="Crime Location" value={crimeLocation} />

              {/* Court and Legal Information */}
              <DetailItem
                label="Court Verdict Decision"
                value={asset.court_verdict_decision_type}
              />
              <DetailItem
                label="Prohibition Court Verdict"
                value={asset.prohabition_court_verdict as string}
              />
              <DetailItem
                label="Prosecutor Decision"
                value={asset.prosecutor_decision_type as string}
              />
              <DetailItem
                label="Court Registration Number"
                value={asset.court_registration_number as string}
              />

              {/* Governing Body Information - Only show if exists */}
              {asset.governing_body && (
                <DetailItem
                  label="Governing Body"
                  value={asset.governing_body}
                />
              )}
              {asset.governing_body_contact && (
                <DetailItem
                  label="Governing Body Contact"
                  value={asset.governing_body_contact}
                />
              )}
              {asset.authorizing_body && (
                <DetailItem
                  label="Authorizing Body"
                  value={asset.authorizing_body}
                />
              )}
              {asset.reason_for_governing && (
                <DetailItem
                  label="Reason for Governing"
                  value={asset.reason_for_governing}
                />
              )}
              {asset.governing_duration_start_date && (
                <DetailItem
                  label="Governing Start Date"
                  value={asset.governing_duration_start_date}
                />
              )}
              {asset.governing_duration_end_date && (
                <DetailItem
                  label="Governing End Date"
                  value={asset.governing_duration_end_date}
                />
              )}

              {/* Asset Recovery Information - Only show if exists */}

              {asset.has_asset_recovery_decision && (
                <DetailItem
                  label="Has Asset Recovery Decision"
                  value={asset.has_asset_recovery_decision ? "Yes" : "No"}
                />
              )}
            </div>
            {Array.isArray(asset?.asset_owner_details) &&
              asset.asset_owner_details.length > 0 && (
                <div>
                  <h3 className="asset-detail-title mb-3">Asset Owners</h3>
                  <div className=" p-2 flex flex-wrap ">
                    {asset.asset_owner_details.map((owner) => (
                      <AssetOwnerCard
                        person={owner?.person}
                        name={owner?.person?.full_name}
                        age={` ${owner?.person?.subcity ?? ""} / ${
                          owner?.person?.region ?? ""
                        } / ${owner?.person?.phone ?? ""}`}
                        key={owner?.id}
                      />
                    ))}
                  </div>
                </div>
              )}
            {children}
          </>
        ) : (
          <p className="text-gray-500 text-center">
            No asset details available
          </p>
        )}
      </div>
    </BasePopup>
  );
}
